import React, { useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";

const LocationSchema = z.object({
  nom: z.string().min(1, "Nom requis"),
  email: z.string().email("Email invalide"),
  phone: z.string().min(1, "Téléphone requis"),
  message: z.string().min(1, "Message requis"),
});

type LocationFormValues = z.infer<typeof LocationSchema>;

const Location: React.FC = () => {
  const { toast } = useToast();
  const form = useForm<LocationFormValues>({
    resolver: zodResolver(LocationSchema),
    defaultValues: { nom: "", email: "", phone: "", message: "" },
    mode: "onTouched",
  });

  const title = "Location | Mon Copieur";
  const description = "Location de copieurs et imprimantes professionnels.";

  useEffect(() => {
    document.title = title;
    let metaTag = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaTag) {
      metaTag = document.createElement("meta");
      metaTag.name = "description";
      document.head.appendChild(metaTag);
    }
    metaTag.content = description;
  }, []);

  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Location Mon Copieur",
    url: "/location",
    publisher: {
      "@type": "Organization",
      name: "Mon Copieur",
      logo: "/lovable-uploads/d5b011bc-34ae-449a-8ec6-8e13ea03ceae.png",
    },
  }), []);

  const onSubmit = async (values: LocationFormValues) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const payload = {
        contact_name: values.nom,
        email: values.email,
        phone: values.phone,
        subject: values.nom,
        message: values.message,
        tags: "Location",
      };

      const res = await fetch("https://moncopieur.cloud-erp.ma/ws/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const contentType = res.headers.get("content-type") || "";
      const data: any = contentType.includes("application/json") ? await res.json() : await res.text();
      const success = typeof data === "object" && (data?.success === true || data?.success === "True" || data?.success === 1);

      if (success) {
        toast({
          title: "Demande envoyée avec succès",
          variant: "success",
        });
        form.reset();
      } else {
        throw new Error((typeof data === "object" && data?.message) || "Échec de l'envoi");
      }
    } catch (error: any) {
      toast({
        title: "Une erreur est survenue",
        description: error?.name === "AbortError" ? "Délai dépassé. Veuillez réessayer plus tard." : "Impossible d'envoyer votre demande. Veuillez réessayer plus tard.",
        variant: "destructive",
      });
      console.error("Location form error:", error);
    } finally {
      clearTimeout(timeoutId);
    }
  };

  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container mx-auto px-4 py-10">
          <h1 className="text-3xl font-semibold">Location</h1>
          <p className="mt-2 text-muted-foreground">{description}</p>
        </div>
      </section>

        <section aria-label="Informations sur la location" className="container mx-auto px-4 py-10">
          <div className="mx-auto max-w-4xl">
            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <h2 className="text-2xl font-semibold mb-4">Location de matériel professionnel</h2>
                <p className="text-muted-foreground mb-6">
                  Profitez de nos solutions de location flexibles pour vos copieurs et imprimantes professionnels. 
                  Des contrats adaptés à vos besoins, avec maintenance incluse.
                </p>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold">Avantages de la location</h3>
                    <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                      <li>• Maintenance et support technique inclus</li>
                      <li>• Renouvellement du matériel garanti</li>
                      <li>• Flexibilité des contrats</li>
                      <li>• Budgétisation simplifiée</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Demande de location</CardTitle>
                </CardHeader>
                <CardContent>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                      <FormField
                        control={form.control}
                        name="nom"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Nom</FormLabel>
                            <FormControl>
                              <Input placeholder="Votre nom complet" {...field} required />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="vous@exemple.com" {...field} required />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Téléphone</FormLabel>
                            <FormControl>
                              <Input placeholder="+212 ..." {...field} required />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Message</FormLabel>
                            <FormControl>
                              <Textarea rows={4} placeholder="Décrivez vos besoins en location..." {...field} required />
                            </FormControl>
                            <FormDescription>Tous les champs sont obligatoires.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                        {form.formState.isSubmitting ? "Envoi..." : "Envoyer la demande"}
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </>
  );
};

export default Location;
